import { defineStore } from 'pinia'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'

import { grpcCall } from '@/grpc-client/grpc/grpc-call'
import { grpcReq } from '@/grpc-client/grpc/grpc-request'
import { $grpcClient } from '@/grpc-client/grpc-client'
import { EventLogger } from '@/utils/EventLogger'

import type { IApiStoreState } from '@/modules/api/types/IApiStoreState'
import type { ILogPayload } from '@/types/ILogPayload'
import { useAuthStore } from '@/modules/auth/stores/authStore'
import type { ITariff } from '@/modules/api/types/ITariff'
// Типизация публичного сервера
import type { IPublicServer } from '@/modules/api/types/IPublicServer'
// Типизация персональной связи пользователя с сервером
import type { IUserToServer } from '@/modules/api/types/IUserToServer'
// Типизация ссылки подписки
import type { ISubscriptionLink } from '@/modules/api/types/ISubscriptionLink'



// Стор используется для взаимодействия между [фронтом <-> API сервером <-> БД 3X-UI] для обеспечения безопасности данных при выполнении запросов

export const useApiStore = defineStore('apiStore', {
  state: (): IApiStoreState => ({
    // Список тарифов
    tariffs: [],
    // Список публичных серверов для пользователей
    publicServers: [],
    // Список персональных связей пользователя с серверами (connectionUrl)
    userToServers: [],
    // Ссылки подписки пользователя (универсальная и диплинки приложений)
    subscriptionLinks: [],
    // Флаг: загрузка серверов уже запущена (защита от дублирования)
    isServersLoading: false,
  }),
  actions: {
    async getTariffs() {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const [ok, response] = await grpcCall(
          (_args, options) => {
            return $grpcClient.TariffEndpoint.getTariffs(grpcReq({}), options)
          },
          'getTariffs'
        )

        if (ok && response && Array.isArray(response.tariffs)) {
          // Сохраняем в стор
          this.tariffs = response.tariffs as ITariff[]
          return true
        }

        if (response && response.error) {
          EventLogger.error('getTariffs', [
            `Ошибка: ${response.error.message}`,
            `Детали: ${response.error.systemMessage}`
          ])
          return false
        }

        EventLogger.error('getTariffs', ['Не удалось выполнить загрузку тарифов через gRPC'])
        return false
      } catch (error: unknown) {
        console.error('getTariffs CATCH:', error)
        EventLogger.error('getTariffs', [`Ошибка getTariffs: ${(error as Error).message}`])
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },
    /**
     * Загружает список публичных серверов для пользователей
     * Использует ServerEndpoint.getPublicServers (требует accessToken)
     *
     * @returns {Promise<boolean>} Успешность загрузки
     */
    async getPublicServers(): Promise<boolean> {
      try {
        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.ServerEndpoint.getPublicServers(grpcReq({}), options),
          'getPublicServers',
        )

        if (!ok || !response || response.error) {
          EventLogger.error('getPublicServers', [
            'Не удалось загрузить публичные серверы',
            response?.error,
          ])
          return false
        }

        this.publicServers = (response.servers || []) as IPublicServer[]
        return true
      } catch (error: unknown) {
        EventLogger.error('getPublicServers', [
          `Catch: ${(error as Error).message}`,
        ])
        return false
      }
    },

    /**
     * Загружает персональные серверы текущего пользователя с ссылками подключения
     * Использует UserToServerEndpoint.getUserToServers (требует accessToken)
     *
     * @returns {Promise<boolean>} Успешность загрузки
     */
    async getUserToServers(): Promise<boolean> {
      try {
        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.UserToServerEndpoint.getUserToServers(grpcReq({}), options),
          'getUserToServers',
        )

        if (!ok || !response || response.error) {
          EventLogger.error('getUserToServers', [
            'Не удалось загрузить серверы пользователя',
            response?.error,
          ])
          return false
        }

        this.userToServers = (response.userToServers || []) as IUserToServer[]
        return true
      } catch (error: unknown) {
        EventLogger.error('getUserToServers', [
          `Catch: ${(error as Error).message}`,
        ])
        return false
      }
    },

    /**
     * Загружает ссылки подписки текущего пользователя (универсальную и диплинки VPN-приложений)
     * Использует SubscriptionEndpoint.getSubscriptionLinks (требует accessToken)
     *
     * @returns {Promise<boolean>} Успешность загрузки
     */
    async getSubscriptionLinks(): Promise<boolean> {
      try {
        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.SubscriptionEndpoint.getSubscriptionLinks(grpcReq({}), options),
          'getSubscriptionLinks',
        )

        if (!ok || !response || response.error) {
          EventLogger.error('getSubscriptionLinks', [
            'Не удалось загрузить ссылки подписки',
            response?.error,
          ])
          return false
        }

        this.subscriptionLinks = (response.links || []) as ISubscriptionLink[]
        return true
      } catch (error: unknown) {
        EventLogger.error('getSubscriptionLinks', [
          `Catch: ${(error as Error).message}`,
        ])
        return false
      }
    },

    /**
     * Загружает публичные серверы и персональные связи параллельно
     * Защита от дублирования: если загрузка уже идёт или данные уже есть — пропускает
     *
     * @returns {Promise<boolean>} Успешность загрузки
     */
    async loadServers(): Promise<boolean> {
      // 1. Загрузка уже идёт — не дублируем
      if (this.isServersLoading) return true

      this.isServersLoading = true
      try {
        const authStore = useAuthStore()

        // Публичные серверы грузим только если их еще нет
        const serversPromise = this.publicServers.length > 0
          ? Promise.resolve(true)
          : this.getPublicServers()

        // Ключи пользователя грузим всегда, если он авторизован
        const userServersPromise = authStore.isAuth
          ? this.getUserToServers()
          : Promise.resolve(true)

        const [serversOk, userServersOk] = await Promise.all([
          serversPromise,
          userServersPromise,
        ])
        return serversOk && userServersOk
      } finally {
        this.isServersLoading = false
      }
    },

    async sendLogToBackend(payload: ILogPayload): Promise<void> {
      try {
        await grpcCall(
          () => $grpcClient.SettingsEndpoint.createLog(payload),
          'createLog'
        )
      } catch (error) {
        console.error('Ошибка при отправк лога на бэк', error)
      }
    },
  },
})
